// @ts-strict-ignore
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, Checkbox, ShowMoreButton, Spacer } from "@aviary";
import type { CategoryFilter } from "@shared/components/CatalogSearch/CatalogSearch";
import { useFavoriteCategoriesInfoQuery } from "@shared/data/queries/FavoriteCategoriesInfoQuery/FavoriteCategoriesInfo.query";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { isFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";
import { isInOfficeCheckoutVar } from "@shared/reactiveVar/useIsInOfficeCheckoutVar/useIsInOfficeCheckoutVar.store";

import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./Favorites.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isDefaultExpanded?: boolean;
}

const SHOW_ALL_CUTOFF = 3;

const Favorites = ({ isBold, logFilterEvent, isDefaultExpanded = false }: Props) => {
  const { t } = useTranslation();
  const { isOnlyShowFavorites, setIsOnlyShowFavorites, activeCategories, setActiveCategories } =
    useCatalogSearch();
  const { isUnauthenticated, isPatient } = useSharedGlobalConfig();
  const isFullscriptJS = isFullscriptJsVar();
  const isInOfficeCheckout = isInOfficeCheckoutVar();
  const [isShowAllButtonNeeded, setIsShowAllButtonNeeded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { data, loading } = useFavoriteCategoriesInfoQuery({
    variables: {
      first: 200,
    },
    skip: isUnauthenticated || isPatient || isFullscriptJS || isInOfficeCheckout,
  });

  const activeFavoriteCategories = activeCategories ?? [];
  const favoriteCategories = data?.currentStore?.categories?.nodes ?? [];

  useEffect(() => {
    if (isShowAllButtonNeeded) {
      return;
    }

    if ((data?.currentStore?.categories?.nodes?.length ?? 0) > SHOW_ALL_CUTOFF) {
      setIsShowAllButtonNeeded(true);
    }
  }, [data?.currentStore?.categories?.nodes]);

  useEffect(() => {
    if (showAll || favoriteCategories.length <= SHOW_ALL_CUTOFF) {
      return;
    }

    // Get index of active favorite categories in the favorite categories list
    const activeCategoryIndexes = activeFavoriteCategories.map(activeCat =>
      favoriteCategories.findIndex(favCat => favCat.id === activeCat.id)
    );

    // If any of the active category index is equal/after SHOW_ALL_CUTOFF, show all
    if (activeCategoryIndexes.some(index => index >= SHOW_ALL_CUTOFF)) {
      setShowAll(true);
    }
  }, [data?.currentStore?.categories?.nodes]);

  useEffect(() => {
    const isAnyCategoryActive = activeFavoriteCategories.length > 0;

    if (isOnlyShowFavorites && isAnyCategoryActive) {
      setIsOnlyShowFavorites(false);
    }
  }, [activeCategories?.length]);

  useEffect(() => {
    if (isOnlyShowFavorites) {
      setActiveCategories?.([]);
    }
  }, [isOnlyShowFavorites]);

  if (isUnauthenticated || isPatient) return null;

  const onChangeOnlyShowFavorites = () => {
    if (!isOnlyShowFavorites) {
      logFilterEvent("true", FilterType.FAVORITES);
    }

    const distanceFromTop = window?.scrollY;
    setIsOnlyShowFavorites(!isOnlyShowFavorites);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const onChangeFavoriteCategory = (clickedCategory: CategoryFilter) => {
    const distanceFromTop = window?.scrollY;

    const isClickedCategoryActive = activeFavoriteCategories.some(
      activeCategory => activeCategory.id === clickedCategory.id
    );

    if (isClickedCategoryActive) {
      setActiveCategories(
        activeFavoriteCategories.filter(activeCategory => activeCategory.id !== clickedCategory.id)
      );

      requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));

      return;
    }

    setActiveCategories([
      ...activeFavoriteCategories,
      { id: clickedCategory.id, name: clickedCategory.name },
    ]);

    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderFavoriteCategories = () => {
    if (loading) {
      return null;
    }

    return favoriteCategories.map((category, index) => {
      if (showAll || index < SHOW_ALL_CUTOFF) {
        return (
          <span key={category.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={activeFavoriteCategories.some(
                activeCategory => activeCategory.id === category.id
              )}
              onChange={() => onChangeFavoriteCategory(category)}
              css={styles.checkbox}
            >
              {category.name}
            </Checkbox>
          </span>
        );
      }

      return null;
    });
  };

  const renderShowAllButton = () => {
    if (!isShowAllButtonNeeded) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  const activeCategoryCount = (isOnlyShowFavorites ? 1 : 0) + activeFavoriteCategories.length;

  if (!isFullscriptJS && !isInOfficeCheckout) {
    return (
      <Accordion
        coreTriggerContent={
          <FilterTitle
            title={t(l.sharedCatalog.filter.Favorites)}
            count={activeCategoryCount}
            isSecondaryWeight={isBold}
            isBadge
          />
        }
        isDefaultExpanded={isDefaultExpanded}
      >
        <span id="allFavoriteCategory" css={[styles.checkbox, styles.allFavoritesCheckbox]}>
          <Checkbox
            isFullWidth
            checked={!!isOnlyShowFavorites}
            onChange={onChangeOnlyShowFavorites}
          >
            {t(l.sharedCatalog.filter.AllFavorites)}
          </Checkbox>
        </span>
        {renderFavoriteCategories()}
        {renderShowAllButton()}
      </Accordion>
    );
  }

  return (
    <Accordion
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Favorites)}
          count={Number(isOnlyShowFavorites)}
          isSecondaryWeight={isBold}
          isBadge
        />
      }
      isDefaultExpanded
    >
      <Checkbox isFullWidth checked={!!isOnlyShowFavorites} onChange={onChangeOnlyShowFavorites}>
        {t(l.sharedCatalog.filter.OnlyShowFavorites)}
      </Checkbox>
    </Accordion>
  );
};

export { Favorites };
